import { css } from '@emotion/core';
import { Icon } from 'antd';
import { sortBy } from 'lodash';
import Router from 'next/router';
import React, { useEffect, useState } from 'react';

import Heading from '@/src/shared/components/Heading';
import LetterPagination from '@/src/shared/components/LetterPagination';
import { bp, colors } from '@/styles';
import request from '@/utils/axios';
import { windowScrollTo } from '@/utils/utils';

import CommitteeCard from './CommitteeCard';
import CommitteeSearch from './CommitteeSearch';

const styles = {
  tileWrapper: css`
    max-width: 970px;
    width: 100%;
    flex: 1 1 auto;
  `,
  header: css`
    font-size: 28px;
    line-height: 36px;
    @media ${bp.xl} {
      font-size: 36px;
    }
  `,
  content: css`
    display: flex;
    flex-direction: row-reverse;
    @media ${bp.md} {
      flex-direction: column;
    }
    @media ${bp.xl} {
      margin-left: 100px;
    }
  `,
  container: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  headerContainer: css`
    @media ${bp.sm} {
      display: flex;
      justify-content: space-between;
      align-content: center;
    }
  `,
  browseHeader: css`
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 24px;
    @media ${bp.md} {
      margin-bottom: -16px;
    }
    @media ${bp.xl} {
      margin-left: 100px;
      font-size: 36px;
      line-height: 48px;
    }
  `,
  lettersWrapper: css`
    flex: 0 1 0;
    width: 40px;
    margin-top: -32px;
    margin-right: -8px;
    @media ${bp.md} {
      width: 100%;
      margin-top: 0;
      margin-right: 0;
    }
    @media ${bp.xl} {
      margin-left: -31px;
    }
  `,
  letterIndexWrapper: css`
    display: flex;
    padding-top: 16px;
  `,
  letterHeader: css`
    color: #3d3d41;
    font-size: 2.8rem;
    font-weight: 900;
  `,
  letterIndex: css`
    margin: 0 0 1.6rem;
    width: 100%;

    @media ${bp.md} {
      align-items: center;
      display: flex;
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: space-between;
    }
  `,
  caretIcon: css`
    font-size: 3.4rem;
    color: ${colors.primaryBlue};
  `,
  topButton: css`
    background: #fff;
    box-shadown: none;
    border: 0.1rem solid ${colors.accentGrayLight};
    border-radius: 0.2rem;
    color: ${colors.secondaryBlue};
    font-size: 1.2rem;
    font-weight: 900;
    letter-spacing: 0;
    line-height: 1.66;
    margin: 0 0 1.6rem;
    padding: 0.5rem 0.8rem 0.6rem;

    &:hover {
      cursor: pointer;
    }

    @media ${bp.md} {
      margin: 0;
    }
  `,
};

const CommitteesList = props => {
  const [committees, setCommittees] = useState([]);
  const { browseHash } = props;
  const listPath = '/policy/committees';
  let prevLetter = '';

  useEffect(() => {
    (async () => {
      const { data } = await request({
        url: 'profile/committeedescriptions',
        triggerLoad: false,
      });
      const activeCommittees = [];
      let nextCommittee = null;
      data.Committees.forEach(committee => {
        if (committee.SubcommitteeFlag === 'N') {
          if (nextCommittee) {
            nextCommittee.subCommittees = nextCommittee.subCommittees
              ? sortBy(nextCommittee.subCommittees, 'Committee')
              : [];
            activeCommittees.push(nextCommittee);
          }
          nextCommittee = committee;
        } else if (nextCommittee.subCommittees) {
          nextCommittee.subCommittees.push(committee);
        } else {
          nextCommittee.subCommittees = [committee];
        }
      });
      activeCommittees.push(nextCommittee);
      setCommittees(sortBy(activeCommittees, 'Committee'));
      windowScrollTo({ top: 0 });
    })();
  }, []);

  const scrollToLetters = () => {
    const letters = document.getElementById('committeeLetters');
    if (letters) {
      const { top } = letters.getBoundingClientRect();
      window.scrollTo({
        left: 0,
        top: top + window.scrollY - 80,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section css={styles.container}>
      <div css={styles.headerContainer}>
        <Heading type={1} theme="primaryGray" css={styles.browseHeader} weight="bolder">
          Browse All
        </Heading>
        <CommitteeSearch committees={committees} />
      </div>
      <div css={styles.content}>
        <div css={styles.lettersWrapper}>
          <LetterPagination
            offeringTitles={committees.map(({ Committee }) => Committee)}
            listPath={listPath}
            size={32}
          />
        </div>
        <div css={styles.tileWrapper}>
          {committees.map((comm, i) => {
            const currentCommittee = comm.Committee;
            const currentLetter = currentCommittee.charAt(0).toUpperCase();
            let before = null;
            if (prevLetter !== currentLetter) {
              prevLetter = currentLetter;
              before = (
                <div css={styles.letterIndexWrapper} id={currentLetter}>
                  <div css={styles.letterIndex}>
                    <button type="button" onClick={scrollToLetters} css={styles.topButton}>
                      <Icon
                        type="up"
                        style={{ fontSize: '12px', margin: '0 4px 0 0', color: '#0075BE' }}
                      />
                      Back to the Top of List
                    </button>

                    <Heading theme="primaryBlue" type={3} weight="bolder">
                      <span css={styles.letterHeader}>{currentLetter}</span>
                    </Heading>
                  </div>
                </div>
              );
            }
            return (
              <React.Fragment key={comm.CommitteeCode}>
                {i > 0 && before}
                <CommitteeCard committee={comm} />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CommitteesList;
